/**
 * Pure logic for the Visual Editor — no DOM, no React, no network.
 * Everything here is unit-tested in a plain node environment (the repo's
 * jsdom setup is broken, so anything DOM-touching lives in editorBridge.ts
 * and is covered by the manual QA checklist instead).
 */
import type {
  EditorFieldKey,
  EditorRegion,
  EditorRegionName,
  EditorSelection,
  EditorSelectionKind,
  EditorStyleVocab,
} from '../../../../types/websiteBuilder';

/** Marker text is stored HTML-attribute-escaped in data-apexos-restore. */
export function decodeRestoreAttr(escaped: string): string {
  return escaped
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Serialized-field cleanup, belt-and-braces after the bridge's DOM pass:
 * strips any leftover editor artifacts. The server re-asserts all of this
 * fail-closed; this exists so honest saves never trip the server checks.
 */
/** The editor's own scaffolding attributes — NOT every data-apexos-* one:
 * generated sites use data-apexos-quote-open to wire the quote drawer, and
 * stripping that would break the button on save. */
export const EDITOR_ARTIFACT_ATTR =
  /data-apexos-(?:restore|kind|slot|hover|selected|empty)(?:-[\w-]+)?/;

export function stripEditorArtifacts(html: string): string {
  const named = 'data-apexos-(?:restore|kind|slot|hover|selected|empty)(?:-[\\w-]+)?';
  return html
    .replace(new RegExp(`\\s(?:${named}|contenteditable|spellcheck)="[^"]*"`, 'g'), '')
    .replace(new RegExp(`\\s(?:${named}|contenteditable|spellcheck)='[^']*'`, 'g'), '')
    .replace(new RegExp(`\\s(?:${named}|contenteditable)(?=[\\s>])`, 'g'), '')
    .replace(/\sclass="([^"]*)"/g, (full, classes: string) => {
      const kept = classes
        .split(/\s+/)
        .filter((c) => c && !c.startsWith('apexos-editor-'))
        .join(' ');
      return kept ? ` class="${kept}"` : '';
    });
}

/**
 * Attributes resolved for display (style/src/href whose original value held an
 * APEXOS image marker) carry the original in data-apexos-restore-<attr>. Put
 * it back verbatim at STRING level — DOM/DOMPurify serialization would
 * entity-escape the marker text inside the attribute, breaking marker parity.
 * Runs AFTER DOMPurify (which must allow the restore attrs through) and
 * BEFORE stripEditorArtifacts. Relies on the restore attr immediately
 * following its target attr, which is how the server emits them.
 */
export function restoreDynamicAttrs(html: string): string {
  // Order-independent and tag-scoped: the sanitiser rebuilds elements and does
  // NOT preserve attribute order, so an adjacency-based match silently fails
  // and the display-resolved value gets saved instead of the original marker.
  return html.replace(/<([a-zA-Z][\w:-]*)([^>]*)>/g, (full, tag: string, attrs: string) => {
    if (!attrs.includes('data-apexos-restore-')) return full;
    let out = attrs;
    (['style', 'src', 'href'] as const).forEach((attr) => {
      const carrier = new RegExp(`\\s*data-apexos-restore-${attr}="([^"]*)"`).exec(out);
      if (!carrier) return;
      const original = decodeRestoreAttr(carrier[1]).replace(/"/g, '&quot;');
      out = out.replace(carrier[0], '');
      const target = new RegExp(`\\s${attr}="[^"]*"`);
      out = target.test(out)
        ? out.replace(target, ` ${attr}="${original}"`)
        : `${out} ${attr}="${original}"`;
    });
    return `<${tag}${out}>`;
  });
}

/** Diff the serialized fields against what the editor loaded — untouched
 * fields are omitted so they can never drift. */
export function diffContent(
  original: Partial<Record<EditorFieldKey, string>>,
  current: Partial<Record<EditorFieldKey, string>>
): Partial<Record<EditorFieldKey, string>> {
  const changed: Partial<Record<EditorFieldKey, string>> = {};
  (Object.keys(current) as EditorFieldKey[]).forEach((key) => {
    if ((current[key] ?? '') !== (original[key] ?? '')) {
      changed[key] = current[key] ?? '';
    }
  });
  return changed;
}

/** Human "what changed" line for the revision history, from op labels. */
export function buildSummary(opLabels: string[]): string {
  const seen: string[] = [];
  opLabels.forEach((label) => {
    if (label && !seen.includes(label)) seen.push(label);
  });
  const text = seen.join('; ');
  return text.length > 490 ? `${text.slice(0, 487)}…` : text || 'Visual edit';
}

/** CSS background value for the section gradient control. */
export function composeGradient(from: string, to: string, angleDeg: number): string {
  return `linear-gradient(${angleDeg}deg, ${from} 0%, ${to} 100%)`;
}

/** Parse a linear-gradient back into its controls (null = not one of ours). */
export function parseGradient(value: string): { from: string; to: string; angleDeg: number } | null {
  const match = /^linear-gradient\((\d+)deg,\s*(.+?)\s+0%,\s*(.+?)\s+100%\)$/.exec(value.trim());
  if (!match) return null;
  return { angleDeg: Number(match[1]), from: match[2], to: match[3] };
}

/** Descriptor the bridge extracts from a clicked element (DOM-free shape). */
export interface ElementDescriptor {
  tag: string;
  classes: string[];
  inlineStyle: string;
  href?: string;
  src?: string;
  alt?: string;
  text?: string;
  /** data-apexos-* attributes present on the element or its nearest carrier */
  restoreMarker?: string;
  lockedKind?: string;
  slot?: string;
  /** nearest annotated field the element sits inside, if any */
  fieldKey?: EditorFieldKey;
  /** which stored source the element sits in (header/footer/template) */
  regionName?: EditorRegionName;
  /** true when the element is inside the chrome (apexos-chrome comments) */
  inChrome: boolean;
  /** whether that region may be edited (server verdict) */
  regionEditable?: boolean;
  /** true when the element is inside the hero region of a child page */
  inHeroRegion: boolean;
  /** true when the element is a top-level <section>-like block */
  isSectionRoot: boolean;
}

const TEXT_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li', 'span', 'blockquote', 'figcaption', 'td', 'th', 'dt', 'dd']);
const BUTTON_TAGS = new Set(['a', 'button']);

/** Classify a click into the sidebar's selection kinds. Degrades to the
 * safest broader kind rather than guessing (see plan: never crash, never
 * block — the server validates saves regardless). */
export function classifySelection(el: ElementDescriptor): EditorSelectionKind {
  if (el.restoreMarker || el.lockedKind) return 'locked';
  // Chrome and layout are editable when the server says the region can be
  // round-tripped; otherwise they fall back to their explanatory panel.
  if (el.inChrome && !el.regionEditable) return 'chrome';
  if (el.tag === 'img') return el.fieldKey ? 'image' : 'chrome';
  if (
    el.fieldKey &&
    (el.fieldKey === 'hero_heading' || el.fieldKey === 'hero_subheading' || el.fieldKey === 'breadcrumb')
  ) {
    return 'heroField';
  }
  if (!el.fieldKey) {
    // No anchor means the markup belongs to a stored source (layout/chrome).
    // Editable regions are treated like ordinary content; the sidebar shows a
    // scope banner. Ineligible ones get their explanatory panel.
    if (el.inHeroRegion) return 'heroField';
    if (!el.regionEditable) return el.inChrome ? 'chrome' : 'template';
  }
  if (BUTTON_TAGS.has(el.tag) && (el.href !== undefined || el.classes.some((c) => c.startsWith('btn')))) {
    return 'button';
  }
  if (el.isSectionRoot) return 'section';
  if (TEXT_TAGS.has(el.tag)) return 'text';
  return 'section';
}

export function toSelection(el: ElementDescriptor): EditorSelection {
  return {
    kind: classifySelection(el),
    fieldKey: el.fieldKey,
    regionName: el.regionName,
    marker: el.restoreMarker,
    slot: el.slot,
    meta: {
      tag: el.tag,
      classes: el.classes,
      inlineStyle: el.inlineStyle,
      href: el.href,
      text: el.text,
      src: el.src,
      alt: el.alt,
      lockedKind: el.lockedKind,
    },
  };
}

/** Is this href a phone link (locked in v1 — business data owns it)? */
export function isTelHref(href: string | undefined): boolean {
  return !!href && href.trim().toLowerCase().startsWith('tel:');
}

/** Swap the section-background class on a class list: remove any class from
 * the vocab's section bucket, add the chosen one ('' = remove only). */
export function swapSectionClass(classes: string[], vocab: EditorStyleVocab, next: string): string[] {
  const kept = classes.filter((c) => !vocab.section_classes.includes(c));
  return next ? [...kept, next] : kept;
}

/** Same for button variant classes. */
export function swapButtonClass(classes: string[], vocab: EditorStyleVocab, next: string): string[] {
  const kept = classes.filter((c) => !vocab.button_classes.includes(c));
  return next ? [...kept, next] : kept;
}

/** localStorage key for crash-restore drafts. */
export function draftStorageKey(projectId: string | number, pageId: string | number): string {
  return `apexos:visual-editor-draft:${projectId}:${pageId}`;
}

/** Scale-to-fit for the device preview (CMS LivePreview pattern). */
export function fitScale(containerWidth: number, deviceWidth: number): number {
  if (containerWidth <= 0 || deviceWidth <= 0) return 1;
  return Math.min(1, containerWidth / deviceWidth);
}

export const DEVICE_WIDTHS = { desktop: 1366, tablet: 768, mobile: 375 } as const;
export type DeviceKind = keyof typeof DEVICE_WIDTHS;


/** Owner-facing description of what an edit in this region will change. */
export function scopeLabel(region: EditorRegion | undefined): string {
  if (!region) return '';
  if (region.scope === 'site') {
    return `Shared by all ${region.affected_pages} pages`;
  }
  if (region.scope === 'shared') {
    return `Shared by ${region.affected_pages} pages`;
  }
  return 'Only on this page';
}

/** Regions whose edits reach beyond the page being edited. */
export function isWideScope(region: EditorRegion | undefined): boolean {
  return !!region && region.scope !== 'page';
}
